import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "react-bootstrap/esm/Alert";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Modal from "react-bootstrap/esm/Modal";
import Spinner from "react-bootstrap/esm/Spinner";
import ChoirSong, { ChoirSongDetails } from "../../DTOs/ChoirSong";
import { createChoirSong, renameChoirSong, SongExistsError, updateChoirSong } from "../../services/choirSongService";
import { parseTags } from "../../utils/repertoire";
import { normalizeSongTitle } from "../../utils/songTitle";

interface SongEditModalProps {
    gemeindeId: string;
    // null = create a new song
    song: ChoirSong | null;
    show: boolean;
    onClose: () => void;
    onSaved: (message: string) => void;
}

const BESETZUNGEN = ["chor", "solo", "orchester"] as const;

/** Create a song, or edit one – including its title (a rename moves its performances). */
export default function SongEditModal({ gemeindeId, song, show, onClose, onSaved }: SongEditModalProps) {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [execution, setExecution] = useState({ chor: true, solo: false, orchester: false });
    const [tags, setTags] = useState("");
    const [songbookNumber, setSongbookNumber] = useState("");
    const [sheetMusicUrl, setSheetMusicUrl] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!show) return;
        setName(song ? song.name.toString() : "");
        setExecution({
            chor: song ? !!song.executionType?.chor : true,
            solo: !!song?.executionType?.solo,
            orchester: !!song?.executionType?.orchester,
        });
        setTags(song ? song.tags.join(", ") : "");
        setSongbookNumber(song?.songbookNumber ?? "");
        setSheetMusicUrl(song?.sheetMusicUrl ?? "");
        setNotes(song?.notes ?? "");
        setError("");
    }, [show, song]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const title = normalizeSongTitle(name);
        if (!title) return;
        const details: ChoirSongDetails = {
            executionType: execution,
            tags: parseTags(tags),
            songbookNumber: songbookNumber.trim(),
            sheetMusicUrl: sheetMusicUrl.trim(),
            notes: notes.trim(),
        };
        setSaving(true);
        setError("");
        try {
            if (!song) {
                await createChoirSong(gemeindeId, title, details);
                onSaved(t("repertoire.created", { name: title }));
            } else {
                let songId = song.id;
                let message = t("repertoire.saved", { name: title });
                if (title !== song.name.toString()) {
                    const result = await renameChoirSong(gemeindeId, song.id, title, false);
                    songId = result.songId;
                    message = t("repertoire.renamed", { name: result.name, count: result.updatedServices });
                }
                await updateChoirSong(gemeindeId, songId, details);
                onSaved(message);
            }
        } catch (err: any) {
            if (err instanceof SongExistsError) {
                setError(String(t("repertoire.exists", { name: err.existingName })));
            } else if (err?.code === "functions/already-exists") {
                setError(String(t("repertoire.existsUseMerge")));
            } else {
                setError(err?.message || t("errors.anErrorOccurred"));
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={saving ? undefined : onClose} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!saving}>
                    <Modal.Title>{song ? t("repertoire.editSong") : t("repertoire.newSong")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group className="mb-3" controlId="songEdit.name">
                        <Form.Label>{t("repertoire.title")}</Form.Label>
                        <Form.Control required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
                        {song && <Form.Text className="text-muted">{t("repertoire.renameHint")}</Form.Text>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="d-block">{t("repertoire.besetzung")}</Form.Label>
                        {BESETZUNGEN.map((key) => (
                            <Form.Check
                                inline
                                key={key}
                                id={`songEdit.${key}`}
                                type="checkbox"
                                label={t(`repertoire.${key}`)}
                                checked={execution[key]}
                                onChange={(e) => setExecution({ ...execution, [key]: e.target.checked })}
                            />
                        ))}
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="songEdit.tags">
                        <Form.Label>{t("repertoire.tags")}</Form.Label>
                        <Form.Control value={tags} onChange={(e) => setTags(e.target.value)} placeholder={String(t("repertoire.tagsPlaceholder"))} />
                        <Form.Text className="text-muted">{t("repertoire.tagsHint")}</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="songEdit.songbookNumber">
                        <Form.Label>{t("repertoire.songbookNumber")}</Form.Label>
                        <Form.Control maxLength={40} value={songbookNumber} onChange={(e) => setSongbookNumber(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="songEdit.sheetMusicUrl">
                        <Form.Label>{t("repertoire.sheetMusicUrl")}</Form.Label>
                        <Form.Control type="url" maxLength={500} value={sheetMusicUrl} onChange={(e) => setSheetMusicUrl(e.target.value)} placeholder="https://…" />
                    </Form.Group>
                    <Form.Group controlId="songEdit.notes">
                        <Form.Label>{t("repertoire.notes")}</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
                    <Button type="submit" disabled={saving}>
                        {saving && <Spinner animation="border" size="sm" className="me-2" />}
                        {t("admin.save")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
